// =========================================================
// * Vue Material Dashboard - v1.4.0
// =========================================================
//
// * Product Page: https://www.creative-tim.com/product/vue-material-dashboard
// * Copyright 2019 Creative Tim (https://www.creative-tim.com)
// * Licensed under MIT (https://github.com/creativetimofficial/vue-material-dashboard/blob/master/LICENSE.md)
//
// * Coded by Creative Tim
//
// =========================================================
//
// * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import { createApp } from "vue";
import { createStore } from "vuex";
import { createRouter, createWebHistory } from "vue-router";
import App from "./App";
import { ISI_API_URL, TITLE_PREFIX } from "./config";

// Tag the browser tab with the environment (e.g. "[dev] iSi Admin Console")
// so dev/testing tabs can't be mistaken for production.
document.title = TITLE_PREFIX + document.title;

// router setup
import routes from "./routes/routes";

// Plugins
import GlobalComponents from "./globalComponents";
import GlobalDirectives from "./globalDirectives";
import Notifications from "./components/NotificationPlugin";
// import SidebarPlugin from "./components/SidebarPlugin";
import axios from "axios";
import { installAuthRefresh, startSessionKeepAlive } from "./utils/authRefresh";
import { installApiFailover } from "./utils/apiFailover";
import { isClientUser, clientAdminGrants } from "./utils/clientAccess";

// The old ?auth_key= localStorage backdoor was removed in the auth refactor —
// it persisted an API key in the browser and bypassed session auth. Headless
// automation sends its API key in the Authorization header directly.
localStorage.removeItem("admin_auth_key");

// Sliding sessions: the isi_token lasts 7 days and every GET /user re-arms
// the full lifetime, so an active session never expires. The 403 interceptor
// heals a token that expired anyway and retries the failed request once; the
// keep-alive (started below, once the store exists) pings /user hourly for
// long-open tabs. See utils/authRefresh.js.
installAuthRefresh();

// If the main API pool is unreachable, retry against the standalone backup
// API host and stick to it until the primary recovers. See utils/apiFailover.js.
installApiFailover();

// Vuetify plugin
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";
import "./assets/css/id-mono.css";
// Must load after vuetify/styles so it can override the .bg-* utilities.
import "./assets/css/theme-fills.css";
// Focus-visible ring + skip link. Never suppress focus outlines.
import "./assets/css/a11y.css";

import * as Chartist from "chartist";

// configure router
const router = createRouter({
  history: createWebHistory(),
  routes,
  linkExactActiveClass: "nav-item active",
});

const store = createStore({
  state: {
    user_data: { email: "", is_admin: false, isi_token: null },
    selected_user: { email: "" },
    user_list: [],
    authInitialized: false,
  },
  getters: {},
  mutations: {
    update_user_list(state, new_data) {
      state.user_list = new_data;
    },
    update_selected_user(state, new_data) {
      state.selected_user = new_data;
    },
    update_user_data(state, new_data) {
      state.user_data = new_data;
    },
    update_purchases(state, new_data) {
      state.purchases = new_data;
    },
    update_products(state, new_data) {
      state.products = new_data;
    },
    set_auth_initialized(state, value) {
      state.authInitialized = value;
    },
  },
  actions: {},
});

// Hourly session keep-alive — needs the store so refreshed user data
// (grants/admin flag) lands in Vuex too, not just the cookie.
startSessionKeepAlive(store);

// Navigation guard: admin-only routes, and the client-user mode (a non-admin
// with client_access grants). Client users land on /dashboard, which renders
// the client portal, and may reach any route declaring requiresClientAccess,
// requiresFileAccess or requiresEncoderAccess. Returns a redirect path or null.
//
// This is REACHABILITY, not authorization — every one of those pages is
// enforced again server-side (isi_data tenancy.js / client_scope.js). Its job
// is to avoid showing a customer a page that would only 403 at them.
function resolveNavigation(to) {
  const userData = store.state.user_data;
  const clientUser = isClientUser(userData);

  // Client users are limited to the pages their grants cover. Authorized by
  // ROUTE META, never path prefixes — a future route joins this mode only by
  // declaring itself, and a mis-tagged route fails closed.
  const clientUserAllowed =
    to.meta.requiresClientAccess ||
    to.meta.requiresClientAdmin ||
    to.meta.requiresFileAccess ||
    to.meta.requiresEncoderAccess;
  if (clientUser && !clientUserAllowed) {
    return "/dashboard";
  }

  if (to.meta.requiresAdmin && !userData.is_admin) {
    return "/dashboard";
  }

  // Pages that administer a client account: staff, or a client_admin on at
  // least one account. Which account is then the server's call.
  if (
    to.meta.requiresClientAdmin &&
    !userData.is_admin &&
    clientAdminGrants(userData).length === 0
  ) {
    return "/dashboard";
  }

  if (
    (to.meta.requiresFileAccess ||
      to.meta.requiresEncoderAccess ||
      to.meta.requiresClientAccess ||
      to.meta.requiresClientAdmin) &&
    !userData.is_admin &&
    !clientUser
  ) {
    return "/dashboard";
  }

  return null;
}

router.beforeEach((to, from, next) => {
  // If auth is not initialized yet, wait for it before deciding.
  if (!store.state.authInitialized) {
    const unwatch = store.watch(
      (state) => state.authInitialized,
      (authInitialized) => {
        if (authInitialized) {
          unwatch(); // Stop watching
          const redirect = resolveNavigation(to);
          if (redirect && redirect !== to.path) {
            next(redirect);
          } else {
            next();
          }
        }
      },
    );
    return; // Don't call next() yet
  }

  const redirect = resolveNavigation(to);
  if (redirect && redirect !== to.path) {
    next(redirect);
  } else {
    next();
  }
});

// Click/page-view telemetry (batched to isi_data POST /activity)
import { installTelemetry } from "./plugins/telemetry";
installTelemetry(router, store);

// Light/dark theming. The starting theme is resolved before Vuetify is
// created — from the saved preference, or the OS preference when the user has
// not chosen one — so the first paint is already the right theme.
import { themes, initialThemeName, installTheme } from "./theme";

// UI language (28 locales). en is bundled; the stored/detected locale's
// message chunk loads right after mount via installI18n — a brief English
// first paint for non-English users is accepted. Vuetify's own strings ride
// its shipped locale packs (vuetifyLocales.js), switched in step by
// setLanguage().
import {
  createAdminI18n,
  installI18n,
  initialLocale,
  vuetifyLocaleName,
  localeRef,
  routeTitle,
} from "./i18n";
import { vuetifyMessages } from "./i18n/vuetifyLocales";
import { watch } from "vue";

const i18n = createAdminI18n();

const vuetify = createVuetify({
  components,
  directives,
  theme: {
    defaultTheme: initialThemeName(),
    themes,
  },
  locale: {
    locale: vuetifyLocaleName(initialLocale()),
    fallback: "en",
    messages: vuetifyMessages,
  },
});

installTheme(vuetify);
installI18n(vuetify);

// Localized document titles (WCAG 2.4.2): re-derive on every navigation AND
// on language change (afterEach alone only fires on navigation).
function applyDocumentTitle(route) {
  const title = routeTitle(route);
  document.title = title
    ? `${TITLE_PREFIX}${title} – iSi Admin Console`
    : `${TITLE_PREFIX}iSi Admin Console`;
}
router.afterEach((to) => applyDocumentTitle(to));
watch(localeRef, () => applyDocumentTitle(router.currentRoute.value));

const app = createApp(App);

app.config.globalProperties.$Chartist = Chartist;
app.config.globalProperties.$http = axios;

app.use(router);
app.use(store);
app.use(vuetify);
app.use(i18n);
app.use(GlobalComponents);
app.use(GlobalDirectives);
app.use(Notifications);
// app.use(SidebarPlugin);

window.isi_v = app.mount("#app");
